/**
 * When the decision recorded in the checkout's own context was taken.
 *
 * The newest-wins safeguard needs something to compare a relayed decision
 * against, and neither Termly nor our own consent provider exposes the moment a
 * decision was made. The moment a decision is first seen in this browsing
 * context is therefore recorded here, keyed by the decision itself: reloading a
 * page with the same decision keeps the original timestamp, while a genuinely
 * changed decision moves it forward. Without that, every reload would look like
 * a brand-new local decision and no relay could ever be newer.
 *
 * Only the purposes are recorded, never anything about the guest.
 */
import type { ConsentCategories } from "../services/cookieConsent/types";

/** `localStorage` key holding the local decision and when it was first seen. */
const LOCAL_DECISION_KEY = "collins-consent-decided-at";

interface StoredDecision {
  /** Epoch milliseconds. */
  readonly at: number;
  /** The decision itself, so an unchanged one does not move the timestamp. */
  readonly purposes: string;
}

/** The purposes a decision speaks about, as a stable, PII-free fingerprint. */
function fingerprint(categories: ConsentCategories): string {
  return [
    categories.analytics,
    categories.performance,
    categories.advertising,
    categories.socialNetworking,
    categories.unclassified,
  ]
    .map((granted) => (granted ? "1" : "0"))
    .join("");
}

function read(): StoredDecision | null {
  try {
    const raw = window.localStorage.getItem(LOCAL_DECISION_KEY);
    if (!raw) return null;

    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;

    const { at, purposes } = parsed as Partial<StoredDecision>;
    if (!Number.isSafeInteger(at) || (at as number) <= 0 || typeof purposes !== "string") return null;

    return { at: at as number, purposes };
  } catch {
    // Storage unavailable or holding something we did not write: no baseline.
    return null;
  }
}

/**
 * Records the local decision, if it is not the one already recorded.
 *
 * @param categories the decision taken in the checkout's own context.
 * @param at when it was seen; defaults to now.
 */
export function recordLocalConsentDecision(categories: ConsentCategories, at: number = Date.now()): void {
  const purposes = fingerprint(categories);
  if (read()?.purposes === purposes) return;

  try {
    const stored: StoredDecision = { at: Math.trunc(at), purposes };
    window.localStorage.setItem(LOCAL_DECISION_KEY, JSON.stringify(stored));
  } catch {
    // Private browsing or blocked storage: the newest-wins baseline is simply
    // absent, which leaves a relayed decision to be judged on its own merits.
  }
}

/** When the local decision was taken, or `null` when none has been recorded. */
export function readLocalConsentDecisionTime(): number | null {
  return read()?.at ?? null;
}

/** Clears the recorded local decision. Test-only. */
export function resetLocalConsentDecision(): void {
  try {
    window.localStorage.removeItem(LOCAL_DECISION_KEY);
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}
